import React, { useMemo, useRef, useState } from 'react';
import { FormulaProcessingCard, FormulaReview } from './FormulaReview';
import { Check, ChevronDown, ChevronRight, FileText, FlaskConical, Info, Leaf, Octagon, PencilLine, Plus, Search, TriangleAlert } from 'lucide-react';
import { Dossier } from '../../data/mockData';
import { RegulationLink } from '../ui/RegulationLink';
interface FormulaTabProps {
  dossier: Dossier;
}
type Verdict = 'Permitted' | 'Permitted with conditions' | 'Exceeds limit' | 'Verification required' | 'Compound ingredient';
type FilterId = 'all' | 'permitted' | 'conditions' | 'exceeds' | 'verification';
type FlowStage = 'verdicts' | 'reading' | 'extracting' | 'review' | 'resolving';
type Ingredient = {
  id: string;
  name: string;
  resolved?: string;
  amount?: string;
  verdict: Verdict;
  limit?: string;
  source?: string;
  exceedsLimit?: boolean;
  withinLimit?: boolean;
  note?: string;
  subIngredients?: Ingredient[];
};
type IngredientGroup = {
  title: string;
  ingredients: Ingredient[];
};
const filters: {
  id: FilterId;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'permitted',
  label: 'Permitted 4'
}, {
  id: 'conditions',
  label: 'Permitted with conditions 4'
}, {
  id: 'exceeds',
  label: 'Exceeds limit 3'
}, {
  id: 'verification',
  label: 'Verification required 1'
}];
const maltodextrinConditions = 'Requires ≥90% fibre content, oral routes only, maximum daily dose ≤50 g, adults only.';
const ingredientGroups: IngredientGroup[] = [{
  title: 'Compound ingredients',
  ingredients: [{
    id: 'orange-juice-powder',
    name: 'Orange Fruit Juice Powder',
    amount: '51.133%',
    verdict: 'Compound ingredient',
    limit: 'Sub-ingredients: 5',
    subIngredients: [{
      id: 'orange-concentrate',
      name: 'Orange juice concentrate',
      resolved: 'ORANGE JUICE',
      amount: '35.661% by part',
      verdict: 'Exceeds limit',
      limit: 'Permitted as an excipient only, in combination with other permitted ingredients, as a flavour. Total flavour concentration must not exceed 5%.',
      source: 'Permissible Ingredients Determination — orange juice, ID 99471'
    }, {
      id: 'sub-resistant-maltodextrin',
      name: 'Soluble corn fibre',
      resolved: 'RESISTANT MALTODEXTRIN',
      amount: '35.661% by part',
      verdict: 'Permitted with conditions',
      limit: maltodextrinConditions,
      source: 'Permissible Ingredients Determination — maltodextrin, ID 93316'
    }, {
      id: 'mandarin-concentrate',
      name: 'Mandarin orange juice concentrate',
      resolved: 'MANDARIN ORANGE JUICE',
      amount: '21.298% by part',
      verdict: 'Exceeds limit',
      limit: 'Counts toward the same 5% total flavour concentration cap.'
    }, {
      id: 'orange-flavour',
      name: 'Natural orange flavour',
      resolved: 'ORANGE 820163 TRE',
      amount: '6.191% by part',
      verdict: 'Verification required',
      limit: 'Proprietary blend. Supplier composition required to confirm the contribution to total flavour concentration.'
    }, {
      id: 'sub-silicon-dioxide',
      name: 'Silicon dioxide',
      resolved: 'SILICON DIOXIDE',
      amount: '1.189% by part',
      verdict: 'Permitted',
      limit: 'No numeric limit defined.'
    }]
  }]
}, {
  title: 'Actives',
  ingredients: [{
    id: 'vitamin-c',
    name: 'Vitamin C (as ascorbic acid)',
    resolved: 'ASCORBIC ACID',
    amount: '550 mg',
    verdict: 'Permitted',
    limit: 'No numeric limit defined.'
  }, {
    id: 'zinc',
    name: 'Zinc (as zinc sulfate)',
    resolved: 'ZINC SULFATE',
    amount: '40 mg, providing 15 mg elemental zinc',
    verdict: 'Permitted with conditions',
    limit: 'Maximum 25 mg elemental zinc per day — within limit.'
  }, {
    id: 'vitamin-a',
    name: 'Vitamin A (as retinol acetate)',
    resolved: 'RETINOL ACETATE',
    amount: '3,600 µg RE',
    verdict: 'Exceeds limit',
    limit: 'Maximum daily dose for internal use is 3,000 micrograms of retinol equivalents. This formulation provides 3,600 µg RE.',
    source: 'Permissible Ingredients Determination — retinol acetate, ID 68280'
  }]
}, {
  title: 'Excipients',
  ingredients: [{
    id: 'resistant-maltodextrin',
    name: 'Soluble corn fibre',
    resolved: 'RESISTANT MALTODEXTRIN',
    amount: '34.291%',
    verdict: 'Permitted with conditions',
    limit: maltodextrinConditions,
    source: 'Permissible Ingredients Determination — maltodextrin, ID 93316'
  }, {
    id: 'silicon-dioxide',
    name: 'Silicon dioxide',
    amount: '1.000%',
    verdict: 'Permitted',
    limit: 'No numeric limit defined.'
  }, {
    id: 'stevia',
    name: 'Stevia extract',
    resolved: 'STEVIA REBAUDIANA',
    amount: '0.500%',
    verdict: 'Permitted',
    limit: 'No restrictions listed.',
    source: 'Permissible Ingredients Determination — Stevia rebaudiana, ID 74288'
  }]
}, {
  title: 'Colours',
  ingredients: [{
    id: 'canthaxanthin',
    name: 'Canthaxanthin (as Lucarotin)',
    resolved: 'CANTHAXANTHIN',
    amount: '0.325%',
    verdict: 'Permitted with conditions',
    limit: 'Permitted for use only as a colour, limited to topical and oral routes of administration.',
    source: 'Permissible Ingredients Determination — canthaxanthin, ID 52583'
  }]
}];
const blockers = [{
  title: 'Reduce total flavour concentration from ~32% to ≤ 5%',
  ingredient: 'Orange fruit juice powder',
  body: 'Orange juice is permitted in listed medicines only as an excipient, in combination with other permitted ingredients, as a flavour — and total flavour concentration in the medicine must not exceed 5%. Orange juice concentrate, mandarin orange juice concentrate and natural orange flavour together account for approximately 32% of this formulation. At this inclusion level the fruit juice powder is the product&apos;s bulk, not a flavour.',
  source: 'Permissible Ingredients Determination — orange juice, ID 99471'
}, {
  title: 'Reduce vitamin A from 3,600 µg RE to ≤ 3,000 µg RE',
  ingredient: 'Retinol acetate',
  body: 'The maximum daily dose of retinol acetate for internal use in listed medicines is 3,000 micrograms of retinol equivalents. This formulation provides 3,600 µg RE per dose.',
  source: 'Permissible Ingredients Determination — retinol acetate, ID 68280'
}];
const recommendations = [{
  title: 'Resolve resistant maltodextrin against the Permissible Ingredients Determination',
  ingredient: 'Soluble corn fibre (Fibersol-2)',
  body: 'Present at 34.3% directly plus a further 35.7% of the orange juice powder compound. At this inclusion level it is a structural component of the formulation, not an excipient, and could not be resolved against the Determination.',
  source: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  title: 'Specify the natural orange flavour components',
  ingredient: 'Natural orange flavour (Orange 820163 TRE)',
  body: 'Proprietary flavour blends must still resolve to permitted ingredients. Request a component breakdown from the supplier to confirm the contribution to total flavour concentration.',
  source: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  title: 'Add the three mandatory vitamin A warning statements',
  ingredient: 'Retinol acetate',
  body: 'Preparations for internal use in adults containing more than 33 micrograms of retinol equivalents per dosage unit require three warning statements. None appear on the artwork. VITA2 and VITA4 must be positioned at the beginning of the directions for use.',
  source: 'Therapeutic Goods (Permissible Ingredients) Determination — retinol acetate, ID 68280',
  statements: [{
    code: 'VITA2',
    text: 'WARNING: If you are pregnant — or considering becoming pregnant — do not take Vitamin A supplements without consulting your doctor or pharmacist.',
    position: 'Position at the beginning of the directions for use.'
  }, {
    code: 'VITA4',
    text: 'WARNING – When taken in excess of 3000 micrograms retinol equivalents – Vitamin A can cause birth defects.',
    position: 'Position at the beginning of the directions for use.'
  }, {
    code: 'VITA3',
    text: 'The recommended daily amount of Vitamin A from all sources is 700 micrograms retinol equivalents for women and 900 micrograms retinol equivalents for men.'
  }]
}, {
  title: 'Justify the vitamin C overage',
  ingredient: 'Vitamin C (ascorbic acid)',
  body: '700 mg of ascorbic acid is formulated per 6 g sachet against a declared 550 mg — an overage of approximately 27%. Overages are acceptable where supported by stability data; provide that justification or reconcile the declaration.',
  source: 'Default standard (BP)'
}];
const considerations = [{
  title: 'Reconcile the declared carbohydrate and fibre figures',
  ingredient: 'Whole formulation',
  body: 'The panel declares 6 g total carbohydrate in a 6 g sachet, but approximately 0.9 g of the sachet is non-carbohydrate material. Declared dietary fibre of 2 g is also low against roughly 3.15 g of resistant maltodextrin in the formulation.'
}];
export function FormulaTab({
  dossier
}: FormulaTabProps) {
  const [activeFilter, setActiveFilter] = useState<FilterId>('all');
  const [search, setSearch] = useState('');
  const [openCompound, setOpenCompound] = useState(true);
  const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({
    'vitamin-a': true
  });
  const [approvedUnknowns, setApprovedUnknowns] = useState<string[]>([]);
  const [flowStage, setFlowStage] = useState<FlowStage>('verdicts');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const startAnalysis = () => {
    setFlowStage('reading');
    window.setTimeout(() => setFlowStage('extracting'), 1500);
    window.setTimeout(() => setFlowStage('review'), 3000);
  };
  const confirmAnalysis = () => {
    setFlowStage('resolving');
    window.setTimeout(() => setFlowStage('verdicts'), 2000);
  };
  const visibleGroups = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return ingredientGroups.map(group => ({
      ...group,
      ingredients: group.ingredients.filter(ingredient => ingredientMatches(ingredient, activeFilter, normalizedSearch))
    })).filter(group => group.ingredients.length > 0);
  }, [activeFilter, search]);
  const fileInput = <input ref={fileInputRef} type="file" className="hidden" onChange={event => {
    if (event.target.files?.length) startAnalysis();
    event.target.value = '';
  }} data-id="element-560" data-mp="1zvjexh4nh2la" />;
  if (flowStage === 'reading' || flowStage === 'extracting' || flowStage === 'resolving') {
    const message = flowStage === 'reading' ? 'Reading formulation…' : flowStage === 'extracting' ? 'Extracting ingredients…' : 'Resolving against the Permissible Ingredients Determination…';
    return <>{fileInput}<FormulaProcessingCard message={message} data-id="element-561" data-mp="2dsyal3lhy6lp" /></>;
  }
  if (flowStage === 'review') {
    return <>{fileInput}<FormulaReview onCancel={() => setFlowStage('verdicts')} onConfirm={confirmAnalysis} data-id="element-562" data-mp="3bag9t7nimm0k" /></>;
  }
  return <div className="space-y-6" data-id="element-563" data-mp="36gu5q3vf3qgd">
      {fileInput}
      <section className="rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="analysis-heading" data-id="element-564" data-mp="3dtt9qm4k212o">
        <div className="flex flex-col gap-4 border-b border-[#e5ebe3] px-6 py-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-565" data-mp="3itfoj0017pdl">
          <div className="flex gap-3" data-id="element-566" data-mp="3nq64zofr0mzj">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-567" data-mp="3sqapdpjs21v1"><Leaf className="h-5 w-5" data-id="element-568" data-mp="nb1qryjnsmh" /></span>
            <div data-id="element-569" data-mp="mhvrt7et75e">
              <h2 id="analysis-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-570" data-mp="kx8monfcqhk">Ingredients analysis</h2>
              <p className="mt-1 text-sm text-[#6e7e72]" data-id="element-571" data-mp="ac1g73art92d">Deterministic per-ingredient verdict (TGA)</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-572" data-mp="fb4kglixfb9a">
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen hover:bg-[#f7faf5]" data-id="element-573" data-mp="kb0qeqb6pcrh"><PencilLine className="h-4 w-4" data-id="element-574" data-mp="oa9ebejjmgf4" />Edit &amp; Re-analyze</button>
            <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen hover:bg-[#f7faf5]" data-id="element-575" data-mp="2ldhyg8yko3vn"><Plus className="h-4 w-4" data-id="element-576" data-mp="2u7eldpzk9kc4" />New analysis</button>
          </div>
        </div>
        <div className="px-6 py-5" data-id="element-577" data-mp="2pdad93ek769g">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" data-id="element-578" data-mp="3437vz3tpz3r9">
            <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 font-semibold text-red-800" data-id="element-579" data-mp="345avcegri98n">High risk</span>
            <span className="font-mono text-[#708174]" data-id="element-580" data-mp="3d4o2h0heylq7">2 Blocker</span><span className="text-[#b4bdb4]" data-id="element-581" data-mp="3d21w3469ec9k">·</span>
            <span className="font-mono text-[#708174]" data-id="element-582" data-mp="393m4emz8gfpt">4 Should fix</span><span className="text-[#b4bdb4]" data-id="element-583" data-mp="1iq5336s64yce">·</span>
            <span className="font-mono text-[#708174]" data-id="element-584" data-mp="1iqwo5no53dof">1 Consider</span>
          </div>
          <p className="mt-4 max-w-5xl text-sm leading-relaxed text-[#506355]" data-id="element-585" data-mp="1epl560zrtqp7">
            This formulation cannot be listed as an AUST L medicine as it stands. Orange juice is permitted only as a flavour within a 5% total concentration cap, and vitamin A exceeds the maximum daily dose for internal use. Both have specific targets to hit.
          </p>
        </div>
      </section>

      <section aria-labelledby="blocker-heading" data-id="element-586" data-mp="zt37752fzong">
        <h2 id="blocker-heading" className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-red-700" data-id="element-587" data-mp="zrs4l8kqgmxx">Blocker · 2</h2>
        <div className="mt-3 space-y-3" data-id="element-588" data-mp="pvgsail57x3h">
          {blockers.map(blocker => <article key={blocker.title} className="rounded-2xl border-2 border-red-300 border-l-[6px] border-l-red-600 bg-red-50 p-5 shadow-sm" data-id="element-589" data-mp="ptm48vekrov6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-590" data-mp="gu8stx6w4q7t">
                <div className="flex gap-3" data-id="element-591" data-mp="269oz94tfjxu0">
                  <Octagon className="mt-0.5 h-5 w-5 shrink-0 fill-red-600 text-red-600" data-id="element-592" data-mp="267vrtnazt9ky" />
                  <div data-id="element-593" data-mp="2b8a118obypkk">
                    <h3 className="font-[Figtree] text-base font-semibold text-red-950" data-id="element-594" data-mp="2b6p3rbamssj0">{blocker.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-red-900" data-id="element-595" data-mp="67pf94rji2gr">{blocker.body}</p>
                  </div>
                </div>
                <IngredientChip data-id="element-596" data-mp="17l0pka6zht6">{blocker.ingredient}</IngredientChip>
              </div>
              <Citation source={blocker.source} data-id="element-597" data-mp="3td6mxs0c8w7t" />
            </article>)}
        </div>
      </section>

      <section aria-labelledby="should-fix-heading" data-id="element-598" data-mp="3odlmfd5isfsz">
        <h2 id="should-fix-heading" className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#708174]" data-id="element-599" data-mp="3of567dqdx8zi">Should fix · 4</h2>
        <div className="mt-3 space-y-3" data-id="element-600" data-mp="3emr3ahnklwbr">
          {recommendations.map(recommendation => <RecommendationCard key={recommendation.title} {...recommendation} data-id="element-601" data-mp="39opjfuiblsz8" />)}
        </div>
      </section>

      <section aria-labelledby="consider-heading" data-id="element-602" data-mp="35ncf5ouvlxuf">
        <h2 id="consider-heading" className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#708174]" data-id="element-603" data-mp="35ox6l5lw90o2">Consider · 2</h2>
        <div className="mt-3 space-y-3" data-id="element-604" data-mp="1g68elsqz3wna">
          {considerations.map(consideration => <div key={consideration.title} className="rounded-2xl border border-[#e1e8df] bg-[#fbfdf9] p-5" data-id="element-605" data-mp="1l4s23xl7tghr">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-606" data-mp="1p6n1gwjl5jsw">
                <div className="flex gap-3" data-id="element-607" data-mp="1u583n1ieedjz"><div className="mt-0.5 h-5 w-5 shrink-0 text-[#78917b]" data-id="element-608" data-mp="1u5q5qerqrlzt" /><div data-id="element-609" data-mp="1u341ao05dmlt"><h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-610" data-mp="xf7irdl5gh7y">{consideration.title}</h3><p className="mt-2 text-sm leading-relaxed text-[#637667]" data-id="element-611" data-mp="xdechzeg7z0h">{consideration.body}</p></div></div>
                <IngredientChip data-id="element-612" data-mp="16g5oc0b8kwsn">{consideration.ingredient}</IngredientChip>
              </div>
            </div>)}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="verdicts-heading" data-id="element-613" data-mp="1b7dyiyke4m6v">
        <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-614" data-mp="dp5hzipfngxt">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between" data-id="element-615" data-mp="ip09noh18ajx">
            <div className="flex flex-wrap items-center gap-2" data-id="element-616" data-mp="1m073wr9h3jbv">
              <Info className="h-5 w-5 text-[#718174]" data-id="element-617" data-mp="1h2dulpcvi97d" />
              <h2 id="verdicts-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-618" data-mp="1h1vrq9vpgwqh">Ingredient verdicts</h2>
              <span className="font-mono text-xs font-semibold uppercase tracking-[0.11em] text-[#6f8174]" data-id="element-619" data-mp="1qrxg72rz0h5x">4 permitted · 4 permitted with conditions · 3 exceeds limit · 1 verification required</span>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between" data-id="element-620" data-mp="y41m85tsd3cu">
            <div className="flex flex-wrap gap-2" data-id="element-621" data-mp="1c3b0v12cfpep">
              {filters.map(filter => <button key={filter.id} type="button" onClick={() => setActiveFilter(filter.id)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${activeFilter === filter.id ? 'border-forest-green bg-forest-green text-white' : 'border-[#d7e0d4] bg-white text-[#617266] hover:bg-[#f5f8f3]'}`} data-id="element-622" data-mp="1747v1eejhf5o">{filter.label}</button>)}
            </div>
            <label className="relative w-full xl:w-64" data-id="element-623" data-mp="2qyu4pvizaea8"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#819083]" data-id="element-624" data-mp="2v1h4qjpsz5ii" /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search ingredients…" className="w-full rounded-lg border border-[#d7e0d4] py-2 pl-9 pr-3 text-sm outline-none focus:border-forest-green focus:ring-2 focus:ring-forest-green/15" data-id="element-625" data-mp="2h2hcz36zuai4" /></label>
          </div>
        </div>

        <div className="space-y-5 p-5" data-id="element-626" data-mp="3epzlqe0nncm4">
          {visibleGroups.length > 0 ? visibleGroups.map(group => <IngredientGroupTable key={group.title} group={group} openCompound={openCompound} onToggleCompound={() => setOpenCompound(open => !open)} expandedDetails={expandedDetails} onToggleDetails={id => setExpandedDetails(details => ({
          ...details,
          [id]: !details[id]
        }))} approvedUnknowns={approvedUnknowns} onMarkOk={id => setApprovedUnknowns(approved => approved.includes(id) ? approved : [...approved, id])} data-id="element-627" data-mp="301ll89qpo08q" />) : <p className="rounded-xl bg-[#f6f8f5] px-4 py-8 text-center text-sm text-[#718174]" data-id="element-628" data-mp="1xf9c0pgd0a1i">No ingredients match this filter.</p>}
        </div>
      </section>

      <p className="sr-only" data-id="element-629" data-mp="1nnddxee9wjxa">Formula source data for {dossier.name}</p>
    </div>;
}
function RecommendationCard({
  title,
  ingredient,
  body,
  source,
  statements
}: {
  title: string;
  ingredient: string;
  body: string;
  source: string;
  statements?: {
    code: string;
    text: string;
    position?: string;
  }[];
}) {
  return <article className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-630" data-mp="2bf0wn91dsooh">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-631" data-mp="27b2xwzfyxfvy">
        <div className="flex gap-3" data-id="element-632" data-mp="22dubgmnsepow"><TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-633" data-mp="22cj9msdwjs0f" /><div data-id="element-634" data-mp="vx8x1ppq2f0t"><h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-635" data-mp="vx8ubhyc80x4">{title}</h3><p className="mt-2 text-sm leading-relaxed text-[#607266]" data-id="element-636" data-mp="qy5t68lhaeuf">{body}</p></div></div>
        <IngredientChip data-id="element-637" data-mp="32l7rp4qlp7i8">{ingredient}</IngredientChip>
      </div>
      {statements && <ul className="mt-4 divide-y divide-[#edf0eb] overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-638" data-mp="32j4xs9mbuu88">
          {statements.map(statement => <li key={statement.code} className="px-4 py-3" data-id="element-639" data-mp="3ble3mm6nhzfa">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6f8174]" data-id="element-640" data-mp="3bjtcl6jjoym6">{statement.code}</p>
              <p className="mt-1.5 font-mono text-xs leading-relaxed text-[#506355]" data-id="element-641" data-mp="3lbz9muonm688">{statement.text}</p>
              {statement.position && <p className="mt-1.5 text-xs font-medium text-amber-800" data-id="element-642" data-mp="3q9r5fzsf6chp">{statement.position}</p>}
            </li>)}
        </ul>}
      <Citation source={source} data-id="element-643" data-mp="3vaojq1ab6mdx" />
    </article>;
}
function IngredientChip({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#f0f3ef] px-2.5 py-1 text-xs font-medium text-[#617266]" data-id="element-644" data-mp="20vvq27szk5om"><Leaf className="h-3.5 w-3.5" data-id="element-645" data-mp="24wpf54i5zqj3" />{children}</span>;
}
function Citation({
  source
}: {
  source: string;
}) {
  return <RegulationLink text={source} className="mt-4" data-id="element-646" data-mp="1os205way7po9" />;
}
function IngredientGroupTable({
  group,
  openCompound,
  onToggleCompound,
  expandedDetails,
  onToggleDetails,
  approvedUnknowns,
  onMarkOk
}: {
  group: IngredientGroup;
  openCompound: boolean;
  onToggleCompound: () => void;
  expandedDetails: Record<string, boolean>;
  onToggleDetails: (id: string) => void;
  approvedUnknowns: string[];
  onMarkOk: (id: string) => void;
}) {
  return <section className="overflow-hidden rounded-xl border border-[#dce3da]" data-id="element-647" data-mp="2cgbgwjtb1n8j">
      <div className="flex items-center justify-between bg-[#eff4ed] px-4 py-3" data-id="element-648" data-mp="2ciej027qf6tm"><h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-649" data-mp="2hgpra8idp99q">{group.title}</h3><span className="font-mono text-xs font-semibold text-[#65776a]" data-id="element-650" data-mp="2mdg8iz5x6awq">{group.ingredients.length} / {group.ingredients.length}</span></div>
      <div className="divide-y divide-[#edf0eb]" data-id="element-651" data-mp="n3huwcer8plz">
        {group.ingredients.map(ingredient => <IngredientRow key={ingredient.id} ingredient={ingredient} openCompound={openCompound} onToggleCompound={onToggleCompound} expandedDetails={expandedDetails} onToggleDetails={onToggleDetails} approvedUnknowns={approvedUnknowns} onMarkOk={onMarkOk} data-id="element-652" data-mp="s0qbi8k98ll4" />)}
      </div>
    </section>;
}
function IngredientRow({
  ingredient,
  openCompound,
  onToggleCompound,
  expandedDetails,
  onToggleDetails,
  approvedUnknowns,
  onMarkOk,
  nested = false
}: {
  ingredient: Ingredient;
  openCompound: boolean;
  onToggleCompound: () => void;
  expandedDetails: Record<string, boolean>;
  onToggleDetails: (id: string) => void;
  approvedUnknowns: string[];
  onMarkOk: (id: string) => void;
  nested?: boolean;
}) {
  const isCompound = ingredient.verdict === 'Compound ingredient';
  const isUnknown = ingredient.verdict === 'Verification required';
  const acknowledged = approvedUnknowns.includes(ingredient.id);
  const detailsOpen = Boolean(expandedDetails[ingredient.id]);
  return <div className={nested ? 'bg-[#fbfdf9]' : 'bg-white'} data-id="element-653" data-mp="398059mtt3pi6">
      <div className={`flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-start lg:justify-between ${nested ? 'pl-8' : ''}`} data-id="element-654" data-mp="219m0bubgvlxr">
        <div className="min-w-0 flex-1" data-id="element-655" data-mp="259wf9wh8lmor">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-656" data-mp="258todojwzk8u">
            <p className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-657" data-mp="1radb1erjqvhq">{ingredient.name}</p>
            {ingredient.resolved && <><span className="text-[#a1ada2]" data-id="element-658" data-mp="1wcccgj3q7ic1">→</span><span className="font-mono text-xs uppercase tracking-[0.08em] text-[#718174]" data-id="element-659" data-mp="1hkto9y0pqhhl">{ingredient.resolved}</span></>}
            {ingredient.amount && <span className="text-sm text-[#718174]" data-id="element-660" data-mp="1mdd1gtvhnalv">— {ingredient.amount}</span>}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-[#748477]" data-id="element-661" data-mp="18mi165z221tw">{ingredient.limit ?? 'No numeric limit defined.'}</p>
          {ingredient.source && <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[#879589]" data-id="element-662" data-mp="ymr888q57uvx">Source: <RegulationLink text={ingredient.source} data-id="element-663" data-mp="13lkjrvlvdbtg" /></p>}
          {ingredient.note && <p className="mt-2 inline-flex rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900" data-id="element-664" data-mp="13nolq807k0r3">{ingredient.note}</p>}
          {!isCompound && <button type="button" onClick={() => onToggleDetails(ingredient.id)} aria-expanded={detailsOpen} className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[#718174] hover:text-forest-green" data-id="element-665" data-mp="fzia0yvo661a"><ChevronRight className={`h-3.5 w-3.5 transition ${detailsOpen ? 'rotate-90' : ''}`} data-id="element-666" data-mp="12ymwpq2hk6yy" />Advanced details</button>}
          {detailsOpen && <div className="mt-2 rounded-lg bg-[#f4f7f2] p-3 text-xs leading-relaxed text-[#607266]" data-id="element-667" data-mp="yw00ze2p21wh">Regulatory matching uses the product&apos;s declared ingredient name, form and quantity. Review the evidence source before making a submission decision.</div>}
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end" data-id="element-668" data-mp="1mmdtf5p6y2rl">
          <VerdictBadge verdict={ingredient.verdict} data-id="element-669" data-mp="1mk9frw1hppe4" />
          {ingredient.exceedsLimit && <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800" data-id="element-670" data-mp="1hpdncuscd00h">Exceeds limit</span>}

          {isUnknown && !acknowledged && <button type="button" onClick={() => onMarkOk(ingredient.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#cddaca] px-2.5 py-1.5 text-xs font-semibold text-forest-green hover:bg-[#f5f8f3]" data-id="element-671" data-mp="3g4upahyk8ore"><Check className="h-3.5 w-3.5" data-id="element-672" data-mp="3pu4g7uho41dx" />Mark as OK</button>}
          {isUnknown && acknowledged && <span className="inline-flex items-center gap-1 text-xs font-semibold text-forest-green" data-id="element-673" data-mp="2s86za1ixs443"><Check className="h-3.5 w-3.5" data-id="element-674" data-mp="2x5ytj27202bg" />Marked OK</span>}
          {isCompound ? <button type="button" onClick={onToggleCompound} aria-expanded={openCompound} className="rounded-lg p-1.5 text-[#718174] hover:bg-[#f2f5f1]" data-id="element-675" data-mp="325l5l8b5bcio"><ChevronDown className={`h-4 w-4 transition ${openCompound ? 'rotate-180' : ''}`} data-id="element-676" data-mp="375pnn2rpcfb0" /></button> : <ChevronDown className="h-4 w-4 text-[#95a297]" data-id="element-677" data-mp="hg5mz0m2yu44" />}
        </div>
      </div>
      {isCompound && openCompound && ingredient.subIngredients && <div className="border-t border-[#e6ece4] bg-[#f5f8f3] px-4 py-3" data-id="element-678" data-mp="mgjsaawarpyg">
          <p className="pl-4 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-679" data-mp="26vyf2jllezdt">Sub-ingredients</p>
          <div className="mt-2 overflow-hidden rounded-lg border border-[#dfe7dc] bg-white" data-id="element-680" data-mp="21zr5mgtzn5r4">
            {ingredient.subIngredients.map(subIngredient => <IngredientRow key={subIngredient.id} ingredient={subIngredient} openCompound={false} onToggleCompound={() => undefined} expandedDetails={expandedDetails} onToggleDetails={onToggleDetails} approvedUnknowns={approvedUnknowns} onMarkOk={onMarkOk} nested data-id="element-681" data-mp="1s8dasmczeu4b" />)}
          </div>
        </div>}
    </div>;
}
function VerdictBadge({
  verdict
}: {
  verdict: Verdict;
}) {
  if (verdict === 'Permitted') return <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-682" data-mp="199rah9pah71g"><Check className="h-3.5 w-3.5" data-id="element-683" data-mp="38l24zrytn1xt" />Permitted</span>;
  if (verdict === 'Permitted with conditions') return <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-684" data-mp="33knw67pebnyq">Permitted with conditions</span>;
  if (verdict === 'Exceeds limit') return <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800" data-id="element-685" data-mp="16pcq505cft4y">Exceeds limit</span>;
  if (verdict === 'Verification required') return <span className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-686" data-mp="wy8f2bisgsfe">Verification required</span>;
  return <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-687" data-mp="1koly5cjebgxu">Compound ingredient</span>;
}
function ingredientMatches(ingredient: Ingredient, filter: FilterId, search: string) {
  const matchesSearch = !search || `${ingredient.name} ${ingredient.resolved ?? ''}`.toLowerCase().includes(search);
  if (!matchesSearch) return false;
  if (filter === 'all') return true;
  const matchesVerdict = (verdict: Verdict) => ingredient.verdict === verdict || Boolean(ingredient.subIngredients?.some(sub => sub.verdict === verdict));
  if (filter === 'permitted') return matchesVerdict('Permitted');
  if (filter === 'conditions') return matchesVerdict('Permitted with conditions');
  if (filter === 'exceeds') return matchesVerdict('Exceeds limit');
  if (filter === 'verification') return matchesVerdict('Verification required');
  return false;
}